import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar,
  GraduationCap,
  MapPin,
  Users,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  MessageCircle,
  Zap,
} from "lucide-react";
import ContactUsDialog from "@/components/ContactUsDialog";
import OpenPlaysLink from "@/components/OpenPlaysLink";

/**
 * /events — public marketing page listing Assenda's upcoming events and
 * coaching sessions. Cards trigger the Contact Us modal because there's no
 * self-serve enrollment flow yet (venue handles inquiries manually).
 *
 * Content curated to the four items requested by the venue owner:
 *   Coaching side  → Group Coaching, Kids Pickleball Clinic
 *   Events side    → Morning Yoga, High Intermediate/Advance OPs
 */

// Featured tournament banners — mirrors the homepage carousel data so
// the same three posters (with the same click hotspots aligned to the
// in-image JOIN buttons) render on this page in a static grid.
const TOURNAMENT_BANNERS = [
  {
    id: "rising-players-cup",
    label: "Rising Players Cup",
    image:
      "https://customer-assets-4nw71qhi.emergentagent.net/job_court-reserve-54/artifacts/07e79fs5_ChatGPT%20Image%20Sep%2020%2C%202026%2C%2001_10_44%20PM.png",
    hotspots: [
      { id: "join-beginner", label: "Join Beginner", href: "https://forms.gle/BQJv8KVZbrG23g8f7", left: 4.5,  top: 84, width: 21, height: 10 },
      { id: "join-novice",   label: "Join Novice",   href: "https://forms.gle/bvWWjvp5zJRqziqe6", left: 26.5, top: 84, width: 21, height: 10 },
    ],
  },
  {
    id: "halloween-flyers",
    label: "Haunted Fun Fair & Spooky '80s",
    wide: true,
    image:
      "https://customer-assets-4nw71qhi.emergentagent.net/job_court-reserve-54/artifacts/0b32fecf7a49dd69_Neon%20Halloween%20Carnival%20and%20%E2%80%9980s%20Disco%20Flyers.png",
    hotspots: [
      { id: "join-haunted-fun-fair", label: "Join Haunted Fun Fair", href: "https://form.jotform.com/assendaevents/haunted-fun-fair",            left: 5.9,  top: 79.8, width: 16.8, height: 7.3 },
      { id: "join-spooky-80s",       label: "Join Spooky 80's",       href: "https://form.jotform.com/assendaevents/assenda-spooky-80s-pickleball", left: 54.3, top: 81.5, width: 16.9, height: 7.2 },
    ],
  },
];

const EVENT_CARDS = [
  {
    id: "morning-yoga",
    title: "Morning Yoga",
    when: "Once a month",
    time: "",
    location: "",
    tags: ["Wellness", "Yoga"],
    image:
      "https://customer-assets-4nw71qhi.emergentagent.net/job_court-reserve-54/artifacts/yuzzny7p_IMG_6976.jpg",
    icon: Sparkles,
    link: "https://form.jotform.com/assendaevents/MorningYoga",
  },
  {
    id: "hi-adv-ops",
    title: "High Intermediate / Advanced OPs",
    when: "",
    time: "",
    location: "",
    tags: ["Open Play", "Competitive"],
    image:
      "https://customer-assets-4nw71qhi.emergentagent.net/job_court-reserve-54/artifacts/fgzbuvj5_IMG_6981.PNG",
    icon: Calendar,
    link: "https://reclub.co/clubs/@assenda",
  },
];

const COACHING_CARDS = [
  {
    id: "group-coaching",
    title: "Group Coaching",
    blurb: "Learn and improve together in a small group.",
    players: "5 – 6 Players",
    icon: Users,
    image:
      "https://customer-assets-4nw71qhi.emergentagent.net/job_court-reserve-54/artifacts/zsm288ne_IMG_6978.jpg",
    link: "https://form.jotform.com/assendaevents/beginners-coaching-session",
  },
  {
    id: "kids-clinic",
    title: "Kids Pickleball Clinic",
    blurb: "Fun and engaging sessions for kids.",
    players: "Ages 6 – 11",
    icon: GraduationCap,
    image:
      "https://customer-assets-4nw71qhi.emergentagent.net/job_court-reserve-54/artifacts/3sqon6zy_IMG_6977.jpg",
    link: "https://form.jotform.com/assendaevents/kids-pickleball-clinic",
  },
];

const EventsPage = () => {
  const navigate = useNavigate();
  const [contactOpen, setContactOpen] = useState(false);
  const openInquiry = () => setContactOpen(true);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <header className="glass-header fixed top-0 left-0 right-0 z-50 px-4 sm:px-6 py-3">
        <div className="max-w-7xl mx-auto flex justify-between items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/")}
            data-testid="events-brand-home"
            className="flex items-center"
          >
            <img
              src="/assenda-logo.png"
              alt="Assenda"
              className="h-8 sm:h-10 w-auto object-contain"
            />
          </button>
          <div className="flex items-center gap-2">
            <OpenPlaysLink />
            <button
              type="button"
              onClick={() => navigate("/availability")}
              data-testid="events-book-court-btn"
              className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-bold bg-[#8cc63f] text-black rounded-sm hover:bg-[#9ed15c] transition-colors inline-flex items-center gap-1.5"
            >
              <Zap size={14} />
              <span className="hidden sm:inline">Book a Court</span>
              <span className="sm:hidden">Book</span>
            </button>
            <button
              type="button"
              onClick={() => navigate("/")}
              data-testid="events-back-home"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs sm:text-sm font-semibold text-white/80 hover:text-[#8cc63f] transition-colors"
            >
              <ArrowLeft size={14} />
              Back
            </button>
          </div>
        </div>
      </header>

      {/* Page body */}
      <main className="pt-24 pb-16 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <p className="label-text mb-2">What&apos;s On</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-2">
            Events &amp; <span className="text-[#8cc63f]">Coaching</span>
          </h1>
          <p className="text-sm text-[#a1a1aa] max-w-2xl mb-10">
            Level up your game and meet the Assenda community. Contact us to
            reserve a slot or ask about upcoming sessions.
          </p>

          {/* Coaching Sessions — shown FIRST per venue preference. */}
          <section className="mb-12">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl sm:text-2xl font-black tracking-tight inline-flex items-center gap-2">
                <GraduationCap size={20} className="text-[#8cc63f]" />
                Coaching Sessions
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {COACHING_CARDS.map((cs) => {
                const IconEl = cs.icon;
                return (
                  <a
                    key={cs.id}
                    href={cs.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`coaching-card-${cs.id}`}
                    className="block text-left card-surface overflow-hidden hover:-translate-y-1 transition-transform focus:outline-none focus:ring-2 focus:ring-[#8cc63f]"
                  >
                    <div
                      className="relative h-40 overflow-hidden bg-[#0f1a08]"
                    >
                      <img
                        src={cs.image}
                        alt={cs.title}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                      <IconEl
                        size={22}
                        className="absolute top-2 right-2 text-white/90 drop-shadow"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-bold mb-1 leading-snug">
                        {cs.title}
                      </h3>
                      <p className="text-[11px] text-[#a1a1aa] mb-2">
                        {cs.blurb}
                      </p>
                      <p className="text-[11px] text-white/80 flex items-center gap-1 mb-3">
                        <Users size={11} className="text-[#8cc63f]" />
                        {cs.players}
                      </p>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-[#8cc63f] flex items-center gap-1">
                        Details &amp; Registration <ArrowRight size={12} />
                      </p>
                    </div>
                  </a>
                );
              })}
            </div>
          </section>

          {/* Upcoming Events */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl sm:text-2xl font-black tracking-tight inline-flex items-center gap-2">
                <Calendar size={20} className="text-[#8cc63f]" />
                Upcoming Events
              </h2>
            </div>

            {/* Featured tournament banners — same posters as the homepage
                carousel, but here they're stacked (no scroll). Each banner
                has invisible click hotspots overlaid on the in-image JOIN
                buttons. */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
              {TOURNAMENT_BANNERS.map((b) => (
                <div
                  key={b.id}
                  data-testid={`event-banner-${b.id}`}
                  className={`relative aspect-[16/9] w-full overflow-hidden rounded-lg border border-white/10 bg-black ${b.wide ? "lg:col-span-2" : ""}`}
                >
                  <img
                    src={b.image}
                    alt={b.label}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  {b.hotspots.map((h) => (
                    <a
                      key={h.id}
                      href={h.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${b.label} — ${h.label}`}
                      data-testid={`event-banner-cta-${b.id}-${h.id}`}
                      className="absolute rounded-full transition-shadow hover:shadow-[0_0_0_4px_rgba(140,198,63,0.55)] focus:outline-none focus:shadow-[0_0_0_4px_rgba(140,198,63,0.75)]"
                      style={{
                        left: `${h.left}%`,
                        top: `${h.top}%`,
                        width: `${h.width}%`,
                        height: `${h.height}%`,
                      }}
                    />
                  ))}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {EVENT_CARDS.map((ev) => {
                const IconEl = ev.icon;
                return (
                  <a
                    key={ev.id}
                    href={ev.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`event-card-${ev.id}`}
                    className="block text-left card-surface overflow-hidden hover:-translate-y-1 transition-transform focus:outline-none focus:ring-2 focus:ring-[#8cc63f]"
                  >
                    <div
                      className="relative h-40 overflow-hidden bg-[#0f1a08]"
                    >
                      <img
                        src={ev.image}
                        alt={ev.title}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                      <IconEl
                        size={22}
                        className="absolute top-2 right-2 text-white/90 drop-shadow"
                      />
                    </div>
                    <div className="p-4">
                      {ev.when && (
                        <p className="text-[10px] font-bold uppercase tracking-widest text-[#8cc63f] mb-1">
                          {ev.when}
                        </p>
                      )}
                      <h3 className="text-sm font-bold mb-1 leading-snug">
                        {ev.title}
                      </h3>
                      {(ev.time || ev.location) && (
                        <p className="text-[11px] text-[#a1a1aa] mb-2">
                          {[ev.time, ev.location].filter(Boolean).join(" · ")}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {ev.tags.map((t) => (
                          <span
                            key={t}
                            className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-[#8cc63f]/10 text-[#8cc63f] border border-[#8cc63f]/25"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-[#8cc63f] inline-flex items-center gap-1">
                        Details &amp; Registration <ArrowRight size={12} />
                      </p>
                    </div>
                  </a>
                );
              })}
            </div>
          </section>

          {/* CTA — dual paths: direct Messenger deep-link + Contact Us
              modal for guests who prefer the form flow. */}
          <div className="mt-12 card-surface p-6 text-center">
            <p className="label-text mb-2">Interested?</p>
            <p className="text-sm text-[#e4e4e7] mb-4">
              Message us to reserve a slot or ask about upcoming events and
              coaching sessions.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://m.me/61577817171416"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="events-cta-messenger"
                className="inline-flex items-center gap-2 px-5 py-3 text-sm font-bold text-white rounded-sm transition-opacity hover:opacity-90"
                style={{ background: "#0084FF" }}
              >
                <MessageCircle size={16} />
                Chat on Messenger
              </a>
              <button
                type="button"
                data-testid="events-cta-contact"
                onClick={openInquiry}
                className="inline-flex items-center gap-2 px-5 py-3 text-sm font-bold text-[#8cc63f] border border-[#8cc63f]/50 rounded-sm hover:bg-[#8cc63f] hover:text-black transition-colors"
              >
                <MapPin size={16} />
                Contact Us
              </button>
            </div>
          </div>
        </div>
      </main>

      <ContactUsDialog open={contactOpen} onOpenChange={setContactOpen} />
    </div>
  );
};

export default EventsPage;
